import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	ChartDataPoint,
	ChartParams,
	ElectricityMeterDetail,
	ElectricityMeterRow,
	TableParams,
} from "./types";
import { mapChartPoints } from "./utils";

export function useElectricityChart(
	params: ChartParams,
): UseQueryResult<ChartDataPoint[], Error> {
	return useQuery({
		queryKey: queryKeys.districts.electricityChart(params),
		queryFn: async (): Promise<ChartDataPoint[]> => {
			const res = await api.get<{ dt: string; sum: number }[]>(
				"/api/districts/electricity_chart/",
				{ params },
			);
			return mapChartPoints(res.data);
		},
	});
}

export function useElectricityTable(
	params: TableParams,
): UseQueryResult<PaginatedResponse<ElectricityMeterRow>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.electricityTable(params),
		queryFn: async (): Promise<PaginatedResponse<ElectricityMeterRow>> => {
			const res = await api.get<PaginatedResponse<ElectricityMeterRow>>(
				"/api/districts/electricity_table/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useElectricityMeterDetail(
	serialNumber: string,
): UseQueryResult<ElectricityMeterDetail, Error> {
	return useQuery({
		queryKey: queryKeys.districts.electricityMeterDetail(serialNumber),
		queryFn: async (): Promise<ElectricityMeterDetail> => {
			const res = await api.get<ElectricityMeterDetail>(
				`/api/districts/electricity_meter_detail/${serialNumber}/`,
			);
			return res.data;
		},
		enabled: !!serialNumber,
	});
}
